<template>
  <RegisterForm
    :title="$t('register.tabs.accountConfig')"
    :desc="$t('verificationStatus.openAccount.accountConfiguration.desc')"
    :nextButtonOnClick="nextButtonOnClick"
    :backButtonOnClick="backButtonOnClick"
    :nextButtonLabel="nextButtonLabel"
    :verticalPadding="verticalPadding"
  >
    <el-form
      class="mt-3 pb-5 md:pb-7 flex flex-col items-stretch justify-start"
      :model="form"
      :rules="rules"
      ref="form"
    >
      <el-form-item prop="tradingPlatform" class="mb-5" :inline-message="true">
        <el-radio-group class="w-full" v-model="form.tradingPlatform" @change="handlePlatformChange">
          <RegisterCollapseRow
            required
            :title="$t('register.accountConfig.chooseTradingPlatform')"
            :infoText="$t(`register.accountConfig.chooseTradingPlatformInfoText`)"
            gap="gap-5"
          >
            <PlatformRadioButton
              v-for="platform in tradingPlatformOptions"
              class="min-w-56"
              :key="platform.key"
              :currValue="form.tradingPlatform"
              :value="platform.value"
            />
          </RegisterCollapseRow>
        </el-radio-group>
      </el-form-item>
      <el-form-item prop="accountType" class="mb-8" :inline-message="true">
        <el-radio-group class="w-full" v-model="form.accountType" @change="accountTypeOnChange">
          <RegisterCollapseRow
            required
            :title="$t('register.accountConfig.chooseAccType')"
            :infoText="$t(`register.accountConfig.chooseAccTypeInfoText`)"
            :expandable="accountTypeOptions.length > 4"
            ><template #default="{ isCollapse }"
              ><RegisterCheckButton
                v-for="accountType in isCollapse ? accountTypeOptions.slice(0, 4) : accountTypeOptions"
                :key="accountType.value"
                :currValue="form.accountType"
                :value="accountType.value"
                :data-testid="accountType.value"
              >
                <img
                  class="h-full w-full object-scale-down"
                  :src="accountType.img"
                /> </RegisterCheckButton></template></RegisterCollapseRow></el-radio-group
      ></el-form-item>
      <div v-if="form.accountType === 'PAMM'" class="mb-5 text-xs text-[#7e90a6] font-bold">
        *PAMM Accounts are only for investors with an existing Money Manager arrangement. PAMM accounts cannot be traded
        by anyone except the Money Manager.
      </div>
      <el-form-item prop="currency" class="mb-8" :inline-message="true">
        <el-radio-group class="w-full" v-model="form.currency">
          <RegisterCollapseRow
            required
            :title="$t('register.accountConfig.chooseAccCurrency')"
            maxHeightTransition="max-h-14"
            gap="gap-2.5 md:gap-x-10 md:gap-y-6"
            ><RegisterCheckButton
              v-for="item in currencyOptions"
              :key="item.currency"
              :currValue="form.currency"
              :value="item.currency"
              :data-testid="item.currency"
              cssClass="text-xs font-bold p-2 md:text-sm md:p-4 md:min-w-35"
            >
              <img class="h-5 w-5 object-scale-down" :src="item.img" />
              <div>{{ $t(item.symbolLabel) }}</div></RegisterCheckButton
            ></RegisterCollapseRow
          ></el-radio-group
        ></el-form-item
      >
      <template v-if="showSetPassword">
        <OpenAccountSetPassword :form="form" />
      </template>
      <el-form-item v-if="showNotesInput" prop="notes" class="m-0 mb-8.5" :inline-message="true">
        <RegisterCollapseRow :title="$t('openAdditAcc.notes')">
          <InputForm
            v-model="form.notes"
            :placeholder="$t('openAdditAcc.noteHere')"
            :autosize="{ minRows: 3 }"
            type="textarea"
            class="w-full"
          />
        </RegisterCollapseRow>
      </el-form-item>
      <div class="flex flex-col justify-start items-start text-start">
        <el-checkbox v-for="template in abTestingTemplates" :key="template.id" v-model="template.checked" class="m-0">
          <span>{{ template.targetContentSetting }}</span>
        </el-checkbox>
      </div>
      <el-form-item prop="checkStatus" class="m-0" :inline-message="true">
        <AccountDisclaimer
          :currChecked="form.checkStatus"
          :checkboxOnChange="value => (this.form.checkStatus = value)"
        />
      </el-form-item>
      <SelectMt4Tips v-model="mt4TipVisible" @onOk="handlePlatformChange" />
    </el-form>
  </RegisterForm>
</template>

<script>
import { apiGetAbTestingEligible, apiSubmitAbTesting } from '@/resource';
import { AbTemplateRule } from '@/constants/abTesting';
import RegisterForm from '../../registerForms/RegisterForm.vue';
import RegisterCheckButton from '../../RegisterCheckButton.vue';
import RegisterCollapseRow from '../../RegisterCollapseRow.vue';
import AccountDisclaimer from './AccountDisclaimer.vue';
import PlatformRadioButton from './PlatformRadioButton.vue';
import SelectMt4Tips from './SelectMt4Tips.vue';
import OpenAccountSetPassword from './OpenAccountSetPassword.vue';

export default {
  name: 'AccountConfigurationQuestionare',
  components: {
    RegisterForm,
    RegisterCollapseRow,
    RegisterCheckButton,
    AccountDisclaimer,
    PlatformRadioButton,
    SelectMt4Tips,
    OpenAccountSetPassword,
  },
  props: {
    form: {
      type: Object,
      required: true,
      default() {
        return {
          tradingPlatform: null,
          accountType: null,
          currency: null,
          checkStatus: false,
          password: null,
          // notes: '' (for OpenLiveAccount)
        };
      }
    },
    rules: {
      type: Object,
      required: true
    },
    tradingPlatformOptions: {
      type: Array,
      required: true
    },
    accountTypeOptions: {
      type: Array,
      required: true
    },
    currencyOptions: {
      type: Array,
      required: true
    },
    tradingPlatformOnChange: {
      type: [Function, null],
      default: null
    },
    accountTypeOnChange: {
      type: [Function, null],
      default: null
    },
    nextButtonLabel: { type: String, default: 'common.button.next' },
    nextButtonOnClick: {
      type: [Function, null],
      default: null
    },
    backButtonOnClick: {
      type: [Function, null],
      default: null
    },
    verticalPadding: {
      type: [String, undefined],
      default: undefined
    },
    // AbTemplateDisplayPage
    abTestingDisplayPage: {
      type: [String, null],
      default: null
    },
    showSetPassword: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return { mt4TipVisible: false, abTestingTemplates: [] };
  },
  computed: {
    showNotesInput() {
      return this.form.notes !== null && this.form.notes !== undefined;
    }
  },
  mounted() {
    if (this.abTestingDisplayPage) {
      this.getAbTestingTemplates();
    }
  },
  methods: {
    handlePlatformChange(value) {
      this.tradingPlatformOnChange?.(value);
      if (value === 'mt4') {
        this.mt4TipVisible = true;
      }
    },
    scrollToErrorField() {
      const errorItem = this.$el.querySelector('.is-error');
      if (errorItem) {
        errorItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    validateField(props, callback) {
      this.$refs.form?.validateField(props, callback);
    },
    validate(callback) {
      this.$refs.form?.validate((valid, rules) => {
        if (!valid) {
          this.$nextTick(() => {
            this.scrollToErrorField();
          });
        }
        callback?.(valid, rules);
      });
    },
    clearValidate(props) {
      this.$refs.form?.clearValidate(props);
    },
    async getAbTestingTemplates() {
      try {
        const res = await apiGetAbTestingEligible(this.abTestingDisplayPage);
        if (res.status === 200 && res.data?.data?.length > 0) {
          this.abTestingTemplates = res.data.data.map(e => {
            return { ...e, checked: e.rules === AbTemplateRule.CLICKED };
          });
        } else {
          this.abTestingTemplates = [];
        }
      } catch (e) {
        console.log(e);
        this.abTestingTemplates = [];
      }
    },
    setAbTestingTemplate(id, checked) {
      const template = this.abTestingTemplates.find(template => template.id === id) ?? null;
      if (template) {
        template.checked = checked;
        this.abTestingTemplates = [...this.abTestingTemplates];
      }
    },
    async submitAbTesting() {
      if (this.abTestingDisplayPage && this.abTestingTemplates.length > 0) {
        await apiSubmitAbTesting(
          this.abTestingTemplates.map(e => {
            return {
              templateId: e.id,
              templateName: e.templateName,
              targetAudienceType: e.targetAudienceType,
              displayPage: this.abTestingDisplayPage,
              rules: e.rules,
              campaignId: e.campaignId,
              campaignName: e.campaignName,
              status: e.checked ? AbTemplateRule.CLICKED : AbTemplateRule.UN_CLICKED
            };
          })
        );
      }
    }
  }
};
</script>
